import { useEffect, useRef } from 'react';
import { Form, type FormInstance } from 'antd';
import { countryAfterCurrencyChange } from '../../../utils/lineItemDefaults';

/* Distinguishes "no currency observed yet" from a currency cleared to undefined,
   so picking one after clearing still moves the country. */
const NOT_SEEN = Symbol('not seen');

/**
 * Keeps a line item's country in step with its currency.
 *
 * When the currency changes, the country follows it (GBP → UK, USD → US) —
 * but only if the country is empty or was itself the old currency's default.
 * A country the trader picked by hand is left alone.
 *
 * The first currency the form reports is not treated as a change: that is the
 * form loading an existing line (or a new line's initial values), and the
 * country it loads with is already the right one.
 *
 * @param form - The line item form.
 */
export const useCountryFollowsCurrency = (form: FormInstance): void => {
  const currency = Form.useWatch('currency', form) as string | undefined;
  const previousCurrency = useRef<string | undefined | typeof NOT_SEEN>(NOT_SEEN);

  useEffect(() => {
    const previous = previousCurrency.current;
    previousCurrency.current = currency;
    if (previous === NOT_SEEN || previous === currency) return;

    const country = countryAfterCurrencyChange(previous, currency, form.getFieldValue('country'));
    if (country !== undefined) {
      form.setFieldsValue({ country: country ?? undefined });
    }
  }, [currency, form]);
};
